import * as React from "react"
import DOMPurify from "dompurify";
import "../inputField.scss"
import "./textInputStyle.scss"
import { classNames } from "../../utils"
import { TInputValidator, IValidatorResult, checkLengthExceed } from "../../utils/validators"
const { useState, useRef, useEffect } = React

export interface ITextInputProps {
    className?: string
    style?: React.CSSProperties
    inputLabel: string
    inputId: string
    initValue?: string | number
    forceUpdate?: { value: string | number }
    validators?: TInputValidator[]
    upperCase?: boolean
    onValidation?: IValidationOutputFunction
    typingHandler?: (value: string) => string
    cluster?: { [key: string]: () => boolean }
    disabled?: boolean
    verified?: boolean
    description?: string
    maxLength?: number
    editable?: boolean
    warningText?: string;
    displayWarning?: boolean;
}

export type IValidationOutputFunction = (
    result: { value: string; inputId: string } & IValidatorResult,
) => void

const TextInput: React.FC<ITextInputProps> = (props) => {
    const {
        className,
        style,
        inputLabel,
        inputId,
        initValue,
        forceUpdate,
        validators,
        upperCase,
        onValidation,
        typingHandler,
        cluster,
        disabled,
        verified,
        description,
        maxLength,
        editable = true,
        warningText,
        displayWarning = false
    } = props

    const inputElem = useRef<HTMLTextAreaElement>(null)
    const [errorMessage, setErrorMessage] = useState("")
    const [isValid, setIsValid] = useState<boolean>(null)
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const [value, setValue] = useState<string | number>(initValue || "")
    const [showWarning, setShowWarning] = React.useState(false);
   



    useEffect(() => {
        if (cluster) { cluster[inputId] = onChangeHandler }
        return () => {
            if (cluster && cluster[inputId]) { delete cluster[inputId] }
        }
    })


    useEffect(() => {
        if (forceUpdate && forceUpdate.value !== undefined) {
            setValue(forceUpdate.value)
            // setIsValid(true)
            // setErrorMessage("")
        }
         
    }, [forceUpdate])

    useEffect(() => {
      
        if (initValue || initValue === "") { setValue(initValue) }


        
        // setIsValid(true)
        resizeTextArea()
        // eslint-disable-next-line
    }, [])

    const onChangeHandler = (event?: React.ChangeEvent) => {
        if(displayWarning){
            setShowWarning(checkLengthExceed(inputElem.current.value, maxLength));
            inputElem.current.value = inputElem.current.value.slice(0, maxLength)
        }

        if (!inputElem.current) return true
        let newValue = upperCase
            ? inputElem.current.value.toUpperCase()
            : inputElem.current.value
        if (!!typingHandler) { newValue = typingHandler(newValue) }
        setValue(newValue)
        inputElem.current.value = newValue

        resizeTextArea()

        if (inputElem.current) {
            const validation = validate({ validators, value: newValue })
            if (!validation) {
                return false
            } else {
                if (onValidation) { onValidation({ ...validation, value: newValue, inputId }) }
            }

            if (validation) {
                /** validation failure */
                if (!event || validation.isValid === true) {
                    setIsValid(validation.isValid)
                    setErrorMessage(validation.message || "")
                }
                if (newValue === "" && validation.isValid === true) { setIsValid(null) }
                return validation.isValid === true
            }

        }

        return false
    }

    const resizeTextArea = () => {
        const elem = inputElem.current
        if (!elem) return
        elem.style.height = "16px"
        elem.style.height = elem.scrollHeight + "px"
    }

    const onFocus = () => {
        setIsFocused(true)
    }

    const onBlur = () => {
        setIsFocused(false)
        setShowWarning(false)
    }

    const isActive = isValid === true || isFocused || verified || value !== ""
    
    return (
        <div className={className} style={style}>
            <div
                className={classNames({
                    "inputField": true,
                    "inputField--warning": isValid === false,
                    "inputField--warningText": showWarning,
                    "inputField--withDescription": !!description,
                    "textInput": true,
                    "textInput--active": isActive,
                })}
                onClick={() => inputElem.current && inputElem.current.focus()}
            >
                <label
                    data-testid="label"
                    className={classNames({
                        "inputField__label": true,
                        "inputField__label--onTop": isActive,
                    })}
                >
                    {`${inputLabel} ${errorMessage}`}
                </label>
                <div className="textInput__content">
                    {editable
                        ? (<><textarea
                            className={classNames({
                                "textInput__input": true,
                                "textInput__input--warning": showWarning 
                            })}
                            maxLength={displayWarning ? undefined : maxLength}
                            name={inputId}
                            data-name={inputId}
                            ref={inputElem}
                            onChange={onChangeHandler}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            value={value}
                            disabled={disabled}
                            rows={1}
                        />
                        {showWarning &&
                        <p className="warning">{warningText}</p>}</>)
                        : <p className="textInput__contentView">
                            {value}
                        </p>
                    }
                </div>

                <div
                    className={classNames({
                        "inputField__icon": true,
                        "inputField__icon--hidden": isValid !== true && isValid !== false && verified !== true,
                        "inputField__icon--isValid": isValid === true,
                        "inputField__icon--isVerified": verified === true,
                        "inputField__icon--isInvalid": isValid === false,
                    })}
                />
            </div>

            {description && (
                <div
                    className={"inputField__description"}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
                ></div>
            )}
        </div>
    )
}

interface IValidateInput {
    validators: ITextInputProps["validators"]
    value: string
}

const validate = ({ validators, value }: IValidateInput) => {
    if (Array.isArray(validators)) {
        for (const validator of validators) {
            const result = validator(value)
            if (!result.isValid) { return result }
        }
    }

    return { isValid: true, message: "" }
}

export default TextInput